import React from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import { COLORS, FONTS, TYPE_SCALE, BUTTON_SPEC, LOGO_RULES, IMAGE_QUALITY, ICON_RULES } from "../brand/tokens";
import { ICON_REGISTRY } from "../brand/icons";
import { BRAND_IMAGES } from "../brand/images";
import BrandLogo from "../components/brand/Logo";

const Swatch = ({ c }) => (
  <div className="panel p-4">
    <div className="h-20 rounded" style={{ background: c.hex, border: "1px solid rgba(201,168,76,0.3)" }} />
    <div className="mt-3 flex items-center justify-between">
      <span className="text-xs text-cream">{c.name}</span>
      <code className="text-[11px] text-gold font-mono">{c.hex}</code>
    </div>
  </div>
);

const BrandStyleGuide = () => (
  <Layout>
    <SEOHead title="Visual Identity System | AuditIQ" path="/brand"
      description="AuditIQ Visual Identity & Asset Generation Engine — colors, typography, iconography, and HD brand imagery." />
    <PageHeader eyebrow="Brand System"
      title="Visual Identity &amp; Asset Generation Engine"
      subtitle="Single source of truth for AuditIQ colors, typography, iconography, dashboards, and HD brand imagery — consumable by the entire platform via /api/brand/tokens." />

    {/* Logo */}
    <section className="max-w-[1400px] mx-auto px-6 py-14">
      <SectionHead num="01" label="Logo" title="Shield, wordmark, and clear-space" />
      <div className="grid md:grid-cols-3 gap-5 mt-8">
        <div className="panel p-8 flex items-center justify-center"><BrandLogo size={56} /></div>
        <div className="panel p-8 flex items-center justify-center bg-cream text-navy">
          <BrandLogo size={56} />
        </div>
        <div className="panel p-6">
          <div className="text-xs uppercase tracking-[0.22em] text-gold mb-3">Rules</div>
          <ul className="text-sm text-cream-dim space-y-2 list-disc pl-5">
            {LOGO_RULES.map(r => <li key={r}>{r}</li>)}
          </ul>
        </div>
      </div>
    </section>

    {/* Colors */}
    <section className="max-w-[1400px] mx-auto px-6 py-10">
      <SectionHead num="02" label="Color Palette" title="Navy · Gold · Cream · Steel" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        {Object.values(COLORS).map(c => <Swatch key={c.hex} c={c} />)}
      </div>
    </section>

    {/* Typography */}
    <section className="max-w-[1400px] mx-auto px-6 py-10">
      <SectionHead num="03" label="Typography" title={`${FONTS.headingPrimary} + ${FONTS.bodyPrimary}`} />
      <div className="grid md:grid-cols-2 gap-6 mt-8">
        {Object.entries(TYPE_SCALE).map(([k, t]) => (
          <div key={k} className="panel p-6 flex items-baseline justify-between gap-4">
            <div style={{ fontSize: Math.min(t.px, 42), fontWeight: t.weight,
              fontFamily: t.family === "display" ? FONTS.headingPrimary : FONTS.bodyPrimary }}>
              Aa — {k}
            </div>
            <div className="text-xs text-steel text-right">
              <div>{t.px}px · {t.weight}</div>
              <div>{t.usage}</div>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* Icons */}
    <section className="max-w-[1400px] mx-auto px-6 py-10">
      <SectionHead num="04" label="Iconography" title="Gold · flat · consistent stroke" />
      <div className="text-xs text-steel mb-4">Stroke width {ICON_RULES.strokeWidth} · color {ICON_RULES.color}</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {ICON_REGISTRY.map(({ key, Component, label }) => (
          <div key={key} className="panel p-5 flex flex-col items-center">
            <Component size={32} />
            <div className="mt-2 text-xs text-cream">{label}</div>
            <code className="text-[10px] text-steel mt-1">{key}</code>
          </div>
        ))}
      </div>
    </section>

    {/* Button */}
    <section className="max-w-[1400px] mx-auto px-6 py-10">
      <SectionHead num="05" label="Buttons" title="Primary gold · ghost gold" />
      <div className="panel p-8 flex flex-wrap gap-4 items-center">
        <button className="btn-gold h-11 px-6 rounded">Primary Action</button>
        <button className="btn-ghost-gold h-11 px-6 rounded">Secondary Action</button>
        <div className="text-xs text-steel ml-4">
          bg {BUTTON_SPEC.background} · text {BUTTON_SPEC.textColor} · radius {BUTTON_SPEC.radius}
        </div>
      </div>
    </section>

    {/* HD Asset Library */}
    <section className="max-w-[1400px] mx-auto px-6 py-10">
      <SectionHead num="06" label="HD Asset Library" title={`Min ${IMAGE_QUALITY.min.w}×${IMAGE_QUALITY.min.h} · ${IMAGE_QUALITY.preferred.w}×${IMAGE_QUALITY.preferred.h} preferred`} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {BRAND_IMAGES.map(img => (
          <figure key={img.key} className="panel overflow-hidden">
            <img src={img.src} alt={img.alt} className="w-full aspect-[16/9] object-cover" loading="lazy" />
            <figcaption className="p-4">
              <div className="text-xs text-gold uppercase tracking-[0.2em]">{img.category.replace("_", " ")}</div>
              <div className="text-sm text-cream mt-1">{img.filename}</div>
              <div className="text-xs text-cream-dim mt-1">{img.alt}</div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>

    <section className="max-w-[1400px] mx-auto px-6 py-12">
      <div className="panel-elev p-8">
        <div className="text-[11px] tracking-[0.28em] uppercase text-gold">API Integration</div>
        <h3 className="font-display text-2xl mt-2">Brand tokens as a service</h3>
        <p className="text-sm text-cream-dim mt-2">
          Any dashboard, report, or generated asset can consume the canonical brand manifest
          via <code className="text-gold bg-navy-deep px-2 py-1 rounded">GET /api/brand/tokens</code>.
          Admins can generate on-brand HD imagery via <code className="text-gold bg-navy-deep px-2 py-1 rounded">POST /api/brand/generate-image</code>
          (Gemini Nano Banana, brand-prompt constrained).
        </p>
      </div>
    </section>
  </Layout>
);

const SectionHead = ({ num, label, title }) => (
  <div>
    <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-gold">
      <span className="inline-block w-6 h-px bg-gold" /> {num} · {label}
    </div>
    <h2 className="font-display text-3xl md:text-4xl mt-3">{title}</h2>
    <div className="gold-rule mt-4" />
  </div>
);

export default BrandStyleGuide;
